import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { useToast } from '@chakra-ui/react';

const API_URL = 'https://blindbox-game-server-dev.uslab.dev';
// const API_URL = 'http://localhost:8080';

// Create axios instance with default config
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL + '/api', // Uses the proxy in package.json (should be port 8080)
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor - can be used to add authentication headers
apiClient.interceptors.request.use(
  (config) => {
    // Add any request modifications here, such as auth tokens
    // Example:
    // const token = localStorage.getItem('token');
    // if (token) {
    //   config.headers.Authorization = `Bearer ${token}`;
    // }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - for handling errors consistently
apiClient.interceptors.response.use(
  (response) => {
    // Any status code within the range of 2xx causes this function to trigger
    console.log(`API Success [${response.config.method?.toUpperCase()} ${response.config.url}]:`, {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      hasData: !!response.data,
      hasNestedData: !!(response.data && response.data.data)
    });
    return response;
  },
  (error: AxiosError) => {
    // Any status codes outside the range of 2xx cause this function to trigger
    if (error.response) {
      // The request was made and the server responded with a status code
      console.error('API Error Response:', error.response.data);
      console.error('Status:', error.response.status);
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error setting up request:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Custom hook to make API calls with loading state
export const useApi = () => {
  const toast = useToast();

  const callApi = async <T>(
    method: string, 
    url: string, 
    data?: any, 
    config?: AxiosRequestConfig
  ): Promise<T> => {
    try {
      let response: AxiosResponse;

      switch (method.toLowerCase()) {
        case 'get':
          response = await apiClient.get(url, config);
          break;
        case 'post':
          response = await apiClient.post(url, data, config);
          break;
        case 'put':
          response = await apiClient.put(url, data, config);
          break;
        case 'delete':
          response = await apiClient.delete(url, config);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      return response.data.data;
    } catch (error: any) {
      // Show toast notification for errors
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    }
  };

  return { callApi };
};

export default apiClient; 